/**
 * Tabs.
 *
 * Headless-ish tab implementation. The caller owns the active id and an
 * onChange callback — works equally well with URL search params, useState,
 * or any other source of truth.
 *
 * Visual: bottom-border indicator (no pills, no rounded blocks — matches
 * the serious reference tone).
 */
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface TabDef<Id extends string> {
  id: Id;
  label: ReactNode;
  /** Optional count rendered as a subtle pill after the label. */
  count?: number;
  disabled?: boolean;
}

export interface TabsProps<Id extends string> {
  tabs: TabDef<Id>[];
  activeId: Id;
  onChange: (id: Id) => void;
  className?: string;
}

export function Tabs<Id extends string>({ tabs, activeId, onChange, className }: TabsProps<Id>) {
  return (
    <div className={cn('border-b border-border-default', className)} role="tablist">
      <div className="-mb-px flex items-center gap-1">
        {tabs.map((tab) => {
          const active = tab.id === activeId;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              disabled={tab.disabled}
              onClick={() => onChange(tab.id)}
              className={cn(
                'inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                active
                  ? 'border-accent text-primary'
                  : 'border-transparent text-secondary hover:text-primary',
                tab.disabled ? 'cursor-not-allowed opacity-50 hover:text-secondary' : '',
              )}
            >
              {tab.label}
              {typeof tab.count === 'number' ? (
                <span
                  className={cn(
                    'inline-flex h-4 min-w-[16px] items-center justify-center rounded-sm px-1 text-[10px] font-semibold tabular-nums',
                    active ? 'bg-accent/10 text-accent' : 'bg-subtle text-secondary',
                  )}
                >
                  {tab.count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
